import { styled } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import dayjs from '../../utils/Dayjs';

const StyledDatePicker = styled(DatePicker, {
  shouldForwardProp: (prop) => prop !== 'isEditableText',
})<{ isEditableText?: boolean }>(({ theme, isEditableText }) =>
  isEditableText
    ? {
        '& .MuiInputBase-root input': {
          padding: '8.5px 14px',
          paddingLeft: theme.spacing(0.5),
          display: 'flex',
          alignItems: 'center',
        },
        '.MuiInputBase-root': {
          marginLeft: theme.spacing(-1),
          paddingLeft: theme.spacing(0.5),
          width: '100%',
        },
        '.MuiInputBase-input': {
          paddingLeft: theme.spacing(0.5),
        },
        '.MuiInputBase-input::-webkit-input-placeholder': {
          visibility: 'hidden',
        },
        '&:hover .MuiInputBase-input::-webkit-input-placeholder': {
          visibility: 'visible',
        },
        '& fieldset': {
          opacity: 0,
        },
        '&:hover fieldset': {
          opacity: 1,
        },
        '& .MuiInputAdornment-root': {
          opacity: 0,
        },
        '&:hover .MuiInputAdornment-root': {
          opacity: 1,
        },
        '.Mui-focused ': {
          ' & .MuiOutlinedInput-notchedOutline': {
            opacity: 1,
          },
        },
      }
    : {
        '& .MuiPickersInputBase-root': { paddingLeft: 0 },
        '& .MuiPickersSectionList-root': { padding: '8.5px 14px' },
      },
);

export const customDatetoIsoDateString = (date: dayjs.Dayjs | undefined | null) => {
  if (!date) {
    return undefined;
  }

  return date.format('YYYY-MM-DD');
};

export const DateFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  isEditableText,
  ...rest
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
}) => {
  if (fieldConfiguration.fieldType !== 'DATE') return;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StyledDatePicker
        className="fs-exclude"
        // This magic is needed to get a data-testid properly assigned
        slotProps={{
          textField: {
            inputProps: {
              'data-testid': 'fim3-custom-field-input-date',
              'data-dd-action-name': 'FIM 3 Custom Field - date picker redacted',
            },
          },
          popper: { disablePortal: true },
        }}
        isEditableText={isEditableText}
        value={values[0] ? dayjs(values[0]) : null}
        onChange={(newValue: dayjs.Dayjs | null) => {
          const newValues = [customDatetoIsoDateString(newValue) ?? ''];
          setValues(newValues);
        }}
        open={rest.autoFocus}
        {...rest}
      />
    </LocalizationProvider>
  );
};
